'use client'
import React from 'react'
import { discardHTMLTags } from '@/constants/hooks'
import { ChevronDown } from 'lucide-react'

interface Props {
    details: string
    title?: string
    showButton?: boolean
    fullDescriptionId?: string  // anchor id to scroll to on "See More"
}

const MerchantDetailsShort = ({
    details,
    title = 'About This Promotion',
    showButton = true,
    fullDescriptionId = 'full-description',
}: Props) => {
    const cleanDetails = discardHTMLTags(details || '').replace(/&nbsp;/gi, ' ').trim()
    const isLong = cleanDetails.length > 180

    if (!details || !cleanDetails || cleanDetails.length === 0) return null

    return (
        <div
            className="rounded-2xl overflow-hidden"
            style={{
                background: '#FFFFFF',
                border: '1px solid rgba(92,122,92,0.18)',
                boxShadow: '0 2px 10px rgba(45,62,45,0.02)',
            }}
        >
            {/* Header */}
            <div className="px-5 py-3.5 border-b border-[rgba(92,122,92,0.1)]">
                <h3 className="text-sm font-black tracking-tight truncate" style={{ color: '#2D3E2D', fontFamily: 'Georgia, serif' }}>
                    {title}
                </h3>
            </div>

            {/* Content — always 2 lines */}
            <div className="px-5 py-4 space-y-3">
                <p className="text-xs sm:text-[13px] leading-relaxed line-clamp-2" style={{ color: 'rgba(45,62,45,0.75)' }}>
                    {cleanDetails}
                </p>

                {/* See More — only if long enough and button enabled */}
                {isLong && showButton && (
                    <a
                        href={`#${fullDescriptionId}`}
                        className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all duration-300 hover:shadow-xs"
                        style={{
                            background: '#E8F0E8',
                            color: '#2D3E2D',
                            border: '1px solid rgba(92,122,92,0.2)',
                        }}
                    >
                        <span>See More</span>
                        <ChevronDown size={13} className="text-[#5C7A5C] group-hover:translate-y-0.5 transition-transform" />
                    </a>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                    .merchant-details-content a {
                        color: #5C7A5C;
                        font-weight: 600;
                        text-decoration: underline;
                        text-decoration-color: rgba(92,122,92,0.3);
                        transition: all 0.2s ease;
                    }
                    .merchant-details-content a:hover {
                        color: #2D3E2D;
                        text-decoration-color: #2D3E2D;
                    }
                    .merchant-details-content strong,
                    .merchant-details-content b {
                        color: #2D3E2D;
                        font-weight: 800;
                    }
                    .merchant-details-content h1,
                    .merchant-details-content h2,
                    .merchant-details-content h3,
                    .merchant-details-content h4,
                    .merchant-details-content h5 {
                        color: #2D3E2D;
                        font-family: Georgia, serif;
                        font-weight: 900;
                        letter-spacing: -0.01em;
                        margin-top: 1.25em;
                        margin-bottom: 0.5em;
                        position: relative;
                        padding-left: 0.75rem;
                        border-left: 3px solid #5C7A5C;
                    }
                    .merchant-details-content h1 { font-size: 1.25rem; }
                    .merchant-details-content h2 { font-size: 1.15rem; }
                    .merchant-details-content h3 { font-size: 1.05rem; }
                    .merchant-details-content h4 { font-size: 0.95rem; }
                    .merchant-details-content p {
                        margin-bottom: 0.85rem;
                        line-height: 1.7;
                    }
                    .merchant-details-content ul,
                    .merchant-details-content ol {
                        margin-left: 1.25rem;
                        margin-bottom: 1rem;
                    }
                    .merchant-details-content ul { list-style-type: disc; }
                    .merchant-details-content ol { list-style-type: decimal; }
                    .merchant-details-content li {
                        margin-bottom: 0.35rem;
                        padding-left: 0.25rem;
                    }
                    .merchant-details-content li::marker { color: #5C7A5C; }
                    .merchant-details-content blockquote {
                        background: #FAF7F2;
                        border-left: 4px solid #F5C542;
                        padding: 0.75rem 1rem;
                        margin: 1rem 0;
                        border-radius: 0 0.75rem 0.75rem 0;
                        font-style: italic;
                        color: #2D3E2D;
                    }
                    .merchant-details-content table {
                        width: 100%;
                        border-collapse: separate;
                        border-spacing: 0;
                        margin: 1rem 0;
                        border-radius: 0.75rem;
                        overflow: hidden;
                        border: 1px solid rgba(92,122,92,0.15);
                    }
                    .merchant-details-content th {
                        background: #E8F0E8;
                        color: #2D3E2D;
                        font-weight: 800;
                        padding: 0.6rem 0.8rem;
                        text-align: left;
                        font-size: 0.8rem;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .merchant-details-content td {
                        padding: 0.6rem 0.8rem;
                        border-top: 1px solid rgba(92,122,92,0.1);
                        font-size: 0.85rem;
                    }
                    .merchant-details-content tr:nth-child(even) { background: #FAF7F2; }
                `
            }} />
        </div>
    )
}

export default MerchantDetailsShort
